import {
	type UseMutationOptions,
	type UseMutationResult,
	useMutation,
} from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { checkAndRecord, type RateLimitConfig } from "@/lib/rate-limit-store";

export const RATE_LIMIT_ERROR = "rate_limited";

export interface RateLimitedMutationExtra {
	isRateLimited: boolean;
	retryAfter: number | null;
}

export function useRateLimitedMutation<
	TData = unknown,
	TError = Error,
	TVariables = void,
	TContext = unknown,
>(
	options: UseMutationOptions<TData, TError, TVariables, TContext>,
	rateLimitConfig: RateLimitConfig & { key: string },
): UseMutationResult<TData, TError, TVariables, TContext> &
	RateLimitedMutationExtra {
	const { key, limit, windowMs, persist } = rateLimitConfig;
	const { t } = useTranslation();

	const [isRateLimited, setIsRateLimited] = useState(false);
	const [retryAfter, setRetryAfter] = useState<number | null>(null);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const clearCountdown = useCallback(() => {
		if (intervalRef.current !== null) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	const startCountdown = useCallback(
		(ms: number) => {
			clearCountdown();
			setRetryAfter(Math.ceil(ms / 1000));
			setIsRateLimited(true);

			intervalRef.current = setInterval(() => {
				setRetryAfter((prev) => {
					if (prev === null || prev <= 1) {
						clearCountdown();
						setIsRateLimited(false);
						return null;
					}
					return prev - 1;
				});
			}, 1000);
		},
		[clearCountdown],
	);

	useEffect(() => clearCountdown, [clearCountdown]);

	const wrappedMutationFn = useCallback(
		async (variables: TVariables) => {
			const { allowed, retryAfter: retryMs } = checkAndRecord(key, {
				limit,
				windowMs,
				persist,
			});

			if (!allowed && retryMs !== null) {
				startCountdown(retryMs);
				const seconds = Math.ceil(retryMs / 1000);
				toast.error(t("errors.tooManyRequests", { seconds }));
				throw new Error("rate_limited");
			}

			if (!options.mutationFn) throw new Error("mutationFn is required");
			return (options.mutationFn as (variables: TVariables) => Promise<TData>)(
				variables,
			);
		},
		[key, limit, windowMs, persist, options.mutationFn, startCountdown, t],
	);

	const mutation = useMutation<TData, TError, TVariables, TContext>({
		...options,
		mutationFn: wrappedMutationFn,
	});

	return { ...mutation, isRateLimited, retryAfter };
}
